// SERVER SAHIFALAGAN ro'yxatlar uchun yagona kontrakt.
//
// ⚠️ NEGA BU FAYL PAYDO BO'LDI (2026-08-20). Backend yettita ro'yxat
// endpointini `List<T>` dan `{rows,total,page,size}` ga o'tkazdi. Panel
// esa ularni hamon `z.array(...)` bilan o'qirdi — natijada oltita sahifa
// UMUMAN ochilmasdi. Nuqsonni hech qanday o'lchov ushlamadi: `tsc -b`
// yashil (Zod ish VAQTIDA yiqiladi), server 200 beradi, backend testlari
// panelni ko'rmaydi.
//
// Shuning uchun shakl, parametr nomlari va tartib enumi endi BITTA
// joyda. Chaqiruvchi faqat "qaysi ustun qaysi enum" xaritasini beradi.
//
// ⚠️ Qorovul: `tests/seam-page-shape.mjs`. U shu yettita endpointni
// chaqiradigan kod `z.array(...)` ga QAYTMASLIGINI tekshiradi.

import { z } from 'zod'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

/**
 * Sahifa konverti — backenddan o'lchangan shakl.
 *
 * ⚠️ `total` MAJBURIY va `nullish` EMAS: bu endpointlar jami sonni
 * ROSTDAN biladi. Uni ixtiyoriy qilish `syntheticTotal` ga qaytishni
 * ochib qo'yardi va jadval "1–25 / 26" degan yolg'on son ko'rsatardi.
 *
 * ⚠️ `page`/`size` javobda bor, lekin panel ularni SO'RAGANINI biladi —
 * shuning uchun o'qiladi (shakl to'liq tekshirilsin), lekin natijaga
 * o'tmaydi.
 */
export function serverPageSchema<Row extends z.ZodTypeAny>(row: Row) {
  return z.object({
    rows: z.array(row),
    total: z.number(),
    page: z.number(),
    size: z.number(),
  })
}

/**
 * Serverdagi eng katta `size`. Kattaroq so'ralsa server 400 beradi.
 *
 * ⚠️ Tanlagichlar (`fetchAllRows`) aynan shu qadam bilan yuradi —
 * kichikroq qadam so'rovlar sonini bekorga oshirardi.
 */
export const MAX_PAGE_SIZE = 200

/**
 * `fetchAllRows` eng ko'pi shuncha so'rov yuboradi (200 × 25 = 5000 qator).
 *
 * ⚠️ Chegara KERAK: server `total` ni noto'g'ri bersa (yoki ro'yxat
 * so'rovlar orasida o'ssa) sikl cheksiz aylanardi va sahifa muzlab
 * qolardi. Chegaraga yetilsa to'plangani qaytariladi — chaqiruvchi
 * bu ro'yxatlarda 5000 dan oshmasligini biladi.
 */
const MAX_PAGES = 25

/** Ustun kaliti → server `sortField` enumi. Ro'yxatda yo'q ustun saralanmaydi. */
export type SortFieldMap = Readonly<Record<string, string>>

export interface ListParamOptions {
  sortFields: SortFieldMap
  /**
   * Qidiruv parametrining nomi.
   *
   * ⚠️ `/api/agents` da u `q`, qolgan oltitasida `search` — bu backend
   * ataylab qoldirgan farq, panel ishlab turgan qidiruvni yo'qotmasin
   * uchun. Sukut `search`.
   */
  searchParam?: string
}

/**
 * Jadval holati → so'rov parametrlari.
 *
 * ⚠️ `page` NOLDAN boshlanadi, `TableState.page` esa BIRDAN (operator
 * ko'radigan raqam). Ayirmani shu yerda qilish uni har chaqiruvchida
 * takrorlashdan qutqaradi — unutilgan `- 1` birinchi sahifani jimgina
 * ikkinchisiga aylantirardi.
 *
 * ⚠️ Tartib TANLANMAGANDA `sortField`/`sortOrder` UMUMAN yuborilmaydi:
 * server sukuti `CREATED_AT DESC`, ya'ni «oxirgi qo'shilgan birinchi».
 * Panel o'z sukutini yuborsa ikkita haqiqat manbai paydo bo'lardi.
 *
 * ⚠️ Xaritada yo'q ustun bo'yicha saralash so'ralsa (eski havola)
 * parametr yuborilmaydi. Noto'g'ri enum serverda **400** beradi va
 * butun sahifa xatoga uchrardi.
 */
export function listParams(state: TableState, options: ListParamOptions): Record<string, unknown> {
  const search = state.search.trim()
  const field = state.sortField === null ? undefined : options.sortFields[state.sortField]
  const sorted = field !== undefined && state.sortOrder !== null

  return {
    page: state.page - 1,
    size: state.pageSize,
    ...(search === '' ? {} : { [options.searchParam ?? 'search']: search }),
    ...(sorted
      ? { sortField: field, sortOrder: state.sortOrder === 'ascend' ? 'ASC' : 'DESC' }
      : {}),
  }
}

/** Sahifa javobi → `DataTable` kutadigan shakl */
export function toPageResult<Row>(
  parsed: { readonly total: number },
  items: readonly Row[],
): PageResult<Row> {
  return { items, total: parsed.total }
}

/**
 * BUTUN ro'yxat — tanlagichlar va ma'lumotnomalar uchun.
 *
 * ⚠️ NEGA BITTA `size=200` SO'ROV YETMAYDI: u 201-yozuvni JIMGINA
 * yo'qotardi va tanlagich «bunday ob'ekt yo'q» deb ko'rsatardi. Aynan
 * shunday jim kesish bu ishni keltirib chiqargan nuqsonlar sinfiga
 * kiradi, shuning uchun sikl `total` ga yetguncha yuradi.
 *
 * ⚠️ Bu funksiya JADVAL uchun EMAS. Jadval `DataTable` + `listParams`
 * orqali FAQAT bir sahifa oladi.
 */
export async function fetchAllRows<Row>(
  loadPage: (page: number, size: number) => Promise<{ rows: readonly Row[]; total: number }>,
): Promise<Row[]> {
  const collected: Row[] = []

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const chunk = await loadPage(page, MAX_PAGE_SIZE)
    collected.push(...chunk.rows)
    if (chunk.rows.length === 0 || collected.length >= chunk.total) break
  }

  return collected
}
